import _ from "lodash";
import React, { useContext, useEffect, useState } from "react";
import { confirmAlert } from "react-confirm-alert";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Alert as Alert2, Button, Col, Dropdown, DropdownItem, DropdownMenu, Input, Row, Table } from "reactstrap";
import { DesktopContainer } from "../layouts";
import { api, constants, dateHelpers, filterHelpers, helpers, UserContext } from "../utils";
import { FilterDateTime, FilterSelect, ItemTypeahead, ServiceThreadSpinner, SortHeader } from "./";

function ChangeDropdownMenu({ pcrId, isActive, attachmentCount }) {
   const navigate = useNavigate();
   const [open, setOpen] = useState(false);

   function downloadForm() {
      api.postDownloadBinary('Report/PrintChangeRequest/' + _.replace(pcrId, 'PCR-', ''), {}, constants.MIME_TYPES.pdf,
         `${pcrId}_${dateHelpers.getDateStringForReportName()}.pdf`).catch((error) => {
            api.catchHandler(error)
            toast.error("There was an error downloading the form");
         })
         .then((res) => {
            if (res?.data === false) {
               toast.error(res.message);
            }
         })
         .catch(api.catchHandler);
   }

   return (
      <td className="data-cell" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)} style={{ position: "relative" }}>
         <span className="shredLink fw-bold noWrap">{pcrId}</span>
         <Dropdown isOpen={open} toggle={() => setOpen(!open)} style={{ position: "absolute", top: "2em", left: "3em" }}>
            <DropdownMenu>
               {isActive ? (
                  <DropdownItem children="Edit Form" onClick={() => navigate(`${constants.ENDPOINTS.processChangeEdit}/${pcrId}`)} />
               ) : (
                  <DropdownItem children="Print Form" onClick={downloadForm} />
               )}
               <DropdownItem className="sort-handle" onClick={() => navigate(`/attachments/6/${pcrId}`)}>
                  Attachments {attachmentCount > -1 ? `(${attachmentCount})` : null}
               </DropdownItem>
            </DropdownMenu>
         </Dropdown>
      </td>);
}

const today = new Date();
const oneYearAgo = dateHelpers.formatDateForServer((new Date(today)).setFullYear(today.getFullYear() - 1));

export default function ProcessChanges(props) {
   const navigate = useNavigate();
   const { currentUser } = useContext(UserContext);
   const [changeList, setChangeList] = useState([]);
   const [filters, setFilters] = useState([
      { filterName: "StartDate", value: oneYearAgo },
      { filterName: 'processType', value: constants.ALL_ITEM },
      { filterName: 'machineType', value: constants.ALL_ITEM },
      { filterName: 'activeOnly', value: true }
   ]);
   const [hasQueried, setHasQueried] = useState(false);
   const [loading, setLoading] = useState(false);
   const [machineTypeList, setMachineTypeList] = useState([]);
   const [message, setMessage] = useState(null);
   const [processTypeList, setProcessTypeList] = useState([]);
   const [sortDirection, setSortDirection] = useState("asc");
   const [sortField, setSortField] = useState("id");
   const [totalExpectedSavings, setTotalExpectedSavings] = useState({});
   const activeOnlyOn = filterHelpers.getValue(filters, "activeOnly");

   useEffect(() => {
      api.fetch("Reference/GetProcessChangeTypes").then((response) => {
         let list = _.map(response.data.processChangeTypes, (m) => {
            m.value = m.id;
            m.label = m.name;
            return m;
         });
         list.unshift(constants.ALL_ITEM);
         setProcessTypeList(list);
      }).catch((error) =>
         setMessage({
            flavor: "alert-danger",
            text: "There was an error loading drop down list data",
         })
      );

      api.fetch("Reference/GetRepairMachineTypes").then((response) => {
         let list = _.map(response.data.machineTypes, (m) => {
            m.value = m.typeId;
            m.label = m.typeName;
            return m;
         });
         list.unshift(constants.ALL_ITEM);
         setMachineTypeList(list);
      }).catch((error) =>
         setMessage({
            flavor: "alert-danger",
            text: "There was an error loading drop down list data",
         })
      );
   }, [])

   useEffect(() => {
      refreshData()
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [sortDirection, sortField, filters])

   function onDeleteRequest(changeId) {
      confirmAlert({
         title: "Confirm Deletion",
         message: 'Are you sure you want to permanently delete this request?',
         buttons: [
            {
               label: "Yes",
               onClick: () => {
                  api.fetch("Reference/DeleteProcessChange/" + changeId).then(r => {
                     if (r.data.success) {
                        refreshData();
                     } else {
                        setMessage({ flavor: "alert-warning", text: r.data.message });
                     }
                  })
                  .catch(api.catchHandler)
                  .finally(() => setLoading(false));
               }
            },
            {
               label: "No"
            }
         ]
      });
   }

   function onFilterChange(changedFilter) {
      const filterChanges = filterHelpers.getFilters(filters, changedFilter);
      setFilters(filterChanges);
   }

   function onSortChange(field, direction) {
      setSortField(field);
      setSortDirection(direction);
   }

   function refreshData() {
      if (loading) return;
      setLoading(true);
      let payload = {
         PCRId: null,
         SortField: sortField,
         SortDirection: sortDirection
      };
      const copy = _.cloneDeep(filters);
      // eslint-disable-next-line no-unused-vars
      let result = _.each(copy, (filter) => {
         if (filter.filterName === "processType" || filter.filterName === "machineType") {
            payload[`${filter.filterName}Id`] = typeof filter.value !== "number"
               ? _.isEmpty(filter.value.value) ? null : filter.value.value
               : filter.value
         }
         else {
            payload[filter.filterName] = filter.value
         }
      });

      api.post("Reference/FindProcessChangeRequests", payload).then((r) => {
         setHasQueried(true);
         setChangeList(r.data.processChanges);
         setTotalExpectedSavings(_.reduce(r.data.processChanges, (total, num) => total + num.expectedSaving, 0))
      })
      .catch(api.catchHandler)
      .finally(() => {
         setLoading(false);
      });
   }

   return (
      document.title = `Process Change Request`,
      <DesktopContainer screenName="Search/Process Change Requests">
         <ServiceThreadSpinner on={loading}>
            {message ? (
               <Row className="mt-3">
                  <Col>
                     <Alert2 className={message.flavor}>{message.text}</Alert2>
                  </Col>
               </Row>
            ) : null}
            <div className="filter-header mb-3">
               <Row>
                  <Col xs={{ size: 6, offset: 3 }}>
                     <Button
                        className="shredButton float-end`"
                        children="Create Request"
                        onClick={() => navigate(`${constants.ENDPOINTS.processChangeEdit}/0`)}
                     />
                  </Col>
               </Row>
               <Row>
                  <Col xs={12}>
                     <Row className="align-items-center mt-2">
                        <Col xs={3}>
                           <h5 children="Start Date:" className="float-end text-white" />
                        </Col>
                        <Col xs={6}>
                           <FilterDateTime
                              filterName="StartDate"
                              value={filterHelpers.getValue(filters, "StartDate")}
                              onChangeCallback={e => onFilterChange(e)}
                           />
                        </Col>
                     </Row>
                     <Row className="align-items-center mt-2">
                        <Col xs={3}>
                           <h5 children="End Date:" className="float-end text-white" />
                        </Col>
                        <Col xs={6}>
                           <FilterDateTime
                              filterName="EndDate"
                              value={filterHelpers.getValue(filters, "EndDate")}
                              onChangeCallback={e => onFilterChange(e)}
                           />
                        </Col>
                     </Row>
                     <Row className="align-items-center mt-2">
                        <Col xs={3}>
                           <h5 children="Process Type:" className="float-end text-white" />
                        </Col>
                        <Col xs={6}>
                           <FilterSelect
                              filterName="processType"
                              options={processTypeList}
                              value={filterHelpers.getValue(filters, "processType")}
                              onChangeCallback={(e) => onFilterChange({
                                 filterName: "processType",
                                 value: e.value,
                                 filteredDisplay: '',
                                 selectValue: e
                              })}
                           />
                        </Col>
                     </Row>
                     <Row className="align-items-center mt-2">
                        <Col xs={3}>
                           <h5 children="Machine Type:" className="float-end text-white" />
                        </Col>
                        <Col xs={6}>
                           <FilterSelect
                              filterName="machineType"
                              options={machineTypeList}
                              value={filterHelpers.getValue(filters, "machineType")}
                              onChangeCallback={(e) => onFilterChange({
                                 filterName: "machineType",
                                 value: e.value,
                                 filteredDisplay: '',
                                 selectValue: e
                              })}
                           />
                        </Col>
                     </Row>
                     <Row className="my-2">
                        <Col xs={3}>
                           <h5 children="Item Number:" className="float-end text-white" />
                        </Col>
                        <Col xs={6}>
                           <ItemTypeahead
                              value={filterHelpers.getValue(filters, "itemNumber")}
                              isClearable
                              onChange={(e) => {
                                 if (e) {
                                    onFilterChange({
                                       filterName: "itemNumber",
                                       value: e.value,
                                       filteredDisplay: '',
                                       selectValue: e
                                    });
                                 } else {
                                    setFilters( _.reject(filters, (x) => x.filterName === "itemNumber") );
                                 }
                              }}
                           />
                        </Col>
                     </Row>
                     <Row className="text-white my-2">
                        <Col xs={{ size: 9, offset: 3 }}>
                           <Input
                              type="checkbox"
                              value={activeOnlyOn}
                              className="ms-1"
                              onChange={() => onFilterChange({
                                 filterName: "activeOnly",
                                 value: !activeOnlyOn,
                                 filteredDisplay: ''
                              })}
                              defaultChecked={activeOnlyOn}
                           />
                           <span className="ps-3">Include Active Requests Only</span>
                        </Col>
                     </Row>
                  </Col>
               </Row>
            </div>
            {changeList && !changeList.length && hasQueried
               ? (<Row><Col>
                  <span style={{ display: "table", margin: "0 auto" }}>There were no requests found for the provided criteria.</span>
                  </Col></Row>
               ) : null
            }
            {changeList && changeList.length
               ? (<Row>
                  <Col xs={12}>
                     <Table className="data-table">
                        <thead className="table-header">
                           <tr>
                              <th>
                                 <SortHeader
                                    displayName="PCR-Id"
                                    field="id"
                                    sortDirection={sortDirection}
                                    sorted={sortField === "id"}
                                    callback={onSortChange}
                                 />
                              </th>
                              <th>
                                 <SortHeader
                                    displayName="Active"
                                    field="isActive"
                                    sortDirection={sortDirection}
                                    sorted={sortField === "isActive"}
                                    callback={onSortChange}
                                 />
                              </th>
                              <th>
                                 <SortHeader
                                    displayName="Created"
                                    field="createdDate"
                                    sortDirection={sortDirection}
                                    sorted={sortField === "createdDate"}
                                    callback={onSortChange}
                                 />
                              </th>
                              <th>
                                 <SortHeader
                                    displayName="Process Type"
                                    field="processTypeString"
                                    sortDirection={sortDirection}
                                    sorted={sortField === "processTypeString"}
                                    callback={onSortChange}
                                 />
                              </th>
                              <th>
                                 <SortHeader
                                    displayName="Machine"
                                    field="machineName"
                                    sortDirection={sortDirection}
                                    sorted={sortField === "machineName"}
                                    callback={onSortChange}
                                 />
                              </th>
                              <th>
                                 <SortHeader
                                    displayName="Expected Savings"
                                    field="expectedSaving"
                                    sortDirection={sortDirection}
                                    sorted={sortField === "expectedSaving"}
                                    callback={onSortChange}
                                 />
                              </th>
                              <th>
                                 <SortHeader
                                    displayName="Items"
                                    field="itemTypeString"
                                    sortDirection={sortDirection}
                                    sorted={sortField === "itemTypeString"}
                                    callback={onSortChange}
                                 />
                              </th>
                              <th>
                                 <SortHeader
                                    displayName="Description"
                                    field="description"
                                    sortDirection={sortDirection}
                                    sorted={sortField === "description"}
                                    callback={onSortChange}
                                 />
                              </th>
                              <th></th>
                           </tr>
                        </thead>
                        <tbody>
                           {_.map(changeList, (b, index) =>
                              <tr key={`b${index}-${b.id}`} className={`tr-${index % 2 === 0 ? "primary" : "secondary"}`}>
                                 <ChangeDropdownMenu
                                    pcrId={b.displayId}
                                    isActive={b.isActive}
                                    attachmentCount={b.processChangeAttachmentCount}
                                 />
                                 <td>{b.isActive ? 'Yes' : 'No'}</td>
                                 <td>{dateHelpers.toShortDateString(b.createdDate)}</td>
                                 <td>{b.processTypeString}</td>
                                 <td>{b.machineName}</td>
                                 <td>{helpers.formatCurrency(b.expectedSaving)}</td>
                                 <td>{_.map(b.itemTypeString.split('\n'), x => (<div key={x}>{x}</div>))}</td>
                                 <td>{b.changeDescriptionTruncated}</td>
                                 {currentUser && (currentUser.isAdmin || currentUser.isPowerUser)
                                    ? (<td className="boldedOrange cursor-pointer" onClick={() => onDeleteRequest(b.id)}>Delete</td>)
                                    : null
                                 }
                              </tr>)
                           }
                        </tbody>
                        <tfoot className="table-footer">
                           <tr>
                              <td colSpan={5}></td>
                              <td>{helpers.formatCurrency(totalExpectedSavings)}</td>
                              <td colSpan={3}></td>
                           </tr>
                        </tfoot>
                     </Table>
                  </Col>
               </Row>)
               : null
            }
         </ServiceThreadSpinner>
      </DesktopContainer>
   );
}
